import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import * as search from "./search/reducer.js";
import * as searchResults from "./searchResults/reducer.js";
import * as flexibleDateMinPrice from "./flexibleDateMinPrice/reducer.js";
import * as searchCriteria from "./searchCriteria/reducer.js";
import * as flightDetails from "./flightDetails/reducer.js";
import * as cartDetails from "./cartDetails/reducer.js";
import * as sanctionScreening from "./sanctionScreening/reducer.js";
import * as extraProducts from "../ancillaries/store/extraProducts/reducer.js";
import * as loadingScreen from "./loadingScreen/reducer.js";
import * as order from "./order/reducer.js";
import * as user from "./user/reducer.js";
import * as popularFlights from "./popularFlights/reducer.js";
import * as ariaLive from "./ariaLive/reducer.js";
import * as metaFlightDetails from "./metaFlightDetails/reducer.js";
import * as travelPurpose from "./travelPurpose/reducer.js";
import * as brandedFares from "./brandedFareOffers/reducer.js";
import * as rebookPolicy from "./rebookPolicy/reducer.js";
import * as paymentCollection from "./paymentCollection/reducer.js";
import * as indexCampaigns from "./indexCampaigns/reducer.js";
import * as cartValidationError from "./cartValidationError/reducer.js";
import * as flyAnywhere from "./flyAnywhere/reducer.js";
import * as flightDeals from "./flightDeals/reducer.js";
import * as flightsInterlinking from "./flightsInterlinking/reducer.js";
import * as searchAiSummary from "./searchAiSummary/reducer.js";
import * as searchBoxState from "./searchBoxState/reducer.js";
import * as searchResultsBounds from "./searchResultsBounds/reducer.js";
import { listenerMiddleware } from "./middlewares.js";
import { whitelabelAncillariesSlice, whitelabelAncillariesInitialState } from "../ancillaries/store/whitelabelAncillaries/whitelabelAncillariesSlice.js";
export const getInitialState = (overrideState) => ({
    search: search.getInitialState(),
    searchResults: searchResults.getInitialState(),
    searchResultsBounds: searchResultsBounds.getInitialState(),
    flexibleDateMinPrice: flexibleDateMinPrice.getInitialState(),
    searchCriteria: searchCriteria.getInitialState(),
    flightDetails: flightDetails.getInitialState(),
    cartDetails: cartDetails.getInitialState(),
    sanctionScreening: sanctionScreening.getInitialState(),
    extraProducts: extraProducts.getInitialState(),
    order: order.getInitialState(),
    loadingScreen: loadingScreen.getInitialState(),
    user: user.getInitialState(),
    popularFlights: popularFlights.getInitialState(),
    ariaLive: ariaLive.getInitialState(),
    metaFlightDetails: metaFlightDetails.getInitialState(),
    travelPurpose: travelPurpose.getInitialState(),
    brandedFares: brandedFares.getInitialState(),
    paymentCollection: paymentCollection.getInitialState(),
    rebookPolicy: rebookPolicy.getInitialState(),
    indexCampaigns: indexCampaigns.getInitialState(),
    cartValidationError: cartValidationError.getInitialState(),
    flyAnywhere: flyAnywhere.getInitialState(),
    flightDeals: flightDeals.getInitialState(),
    flightsInterlinking: flightsInterlinking.getInitialState(),
    searchAiSummary: searchAiSummary.getInitialState(),
    searchBoxState: searchBoxState.getInitialState(),
    whitelabelAncillaries: whitelabelAncillariesInitialState(),
    ...overrideState
});
export function createStore(initialState = {}) {
    return configureStore({
        reducer: {
            search: search.reducer,
            searchResults: searchResults.reducer,
            searchResultsBounds: searchResultsBounds.reducer,
            flexibleDateMinPrice: flexibleDateMinPrice.reducer,
            searchCriteria: searchCriteria.reducer,
            flightDetails: flightDetails.reducer,
            cartDetails: cartDetails.reducer,
            sanctionScreening: sanctionScreening.reducer,
            extraProducts: extraProducts.reducer,
            order: order.reducer,
            loadingScreen: loadingScreen.reducer,
            user: user.reducer,
            popularFlights: popularFlights.reducer,
            ariaLive: ariaLive.reducer,
            metaFlightDetails: metaFlightDetails.reducer,
            travelPurpose: travelPurpose.reducer,
            brandedFares: brandedFares.reducer,
            paymentCollection: paymentCollection.reducer,
            rebookPolicy: rebookPolicy.reducer,
            indexCampaigns: indexCampaigns.reducer,
            cartValidationError: cartValidationError.reducer,
            flyAnywhere: flyAnywhere.reducer,
            flightDeals: flightDeals.reducer,
            flightsInterlinking: flightsInterlinking.reducer,
            searchAiSummary: searchAiSummary.reducer,
            searchBoxState: searchBoxState.reducer,
            whitelabelAncillaries: whitelabelAncillariesSlice.reducer
        },
        devTools: {
            name: "Redux store - Main"
        },
        preloadedState: {
            search: search.getInitialState(),
            searchResults: searchResults.getInitialState(),
            searchResultsBounds: searchResultsBounds.getInitialState(),
            flexibleDateMinPrice: flexibleDateMinPrice.getInitialState(),
            searchCriteria: searchCriteria.getInitialState(),
            flightDetails: flightDetails.getInitialState(),
            cartDetails: cartDetails.getInitialState(),
            sanctionScreening: sanctionScreening.getInitialState(),
            extraProducts: extraProducts.getInitialState(),
            order: order.getInitialState(),
            loadingScreen: loadingScreen.getInitialState(),
            user: user.getInitialState(),
            popularFlights: popularFlights.getInitialState(),
            ariaLive: ariaLive.getInitialState(),
            metaFlightDetails: metaFlightDetails.getInitialState(),
            travelPurpose: travelPurpose.getInitialState(),
            brandedFares: brandedFares.getInitialState(),
            paymentCollection: paymentCollection.getInitialState(),
            rebookPolicy: rebookPolicy.getInitialState(),
            indexCampaigns: indexCampaigns.getInitialState(),
            cartValidationError: cartValidationError.getInitialState(),
            flyAnywhere: flyAnywhere.getInitialState(),
            flightDeals: flightDeals.getInitialState(),
            flightsInterlinking: flightsInterlinking.getInitialState(),
            searchAiSummary: searchAiSummary.getInitialState(),
            searchBoxState: searchBoxState.getInitialState(),
            whitelabelAncillaries: whitelabelAncillariesInitialState(),
            ...(process.env.BUILD_TARGET === "client" && window.__INITIAL_STATE__),
            ...initialState
        },
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().prepend(listenerMiddleware.middleware)
    });
}
const getAll = (state) => state;
export const useStore = () => useSelector(getAll);
export const useActions = (actions) => {
    const dispatch = useDispatch();
    return useMemo(() => {
        const enhancedActions = {};
        Object.entries(actions).forEach(([key, action]) => {
            enhancedActions[key] = (...args) => dispatch(action(...args));
        });
        return enhancedActions;
    }, [dispatch, actions]);
};
